import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import testCategoriesService from '../api/testCategoriesService';
import type {
    TestCategory,
    CreateTestCategoryDto,
    UpdateTestCategoryDto,
} from '../types/types';

export const useTestCategories = () => {
    return useQuery<TestCategory[]>({
        queryKey: ['testCategories'],
        queryFn: () => testCategoriesService.getAll(),
        refetchOnWindowFocus: false,
    });
};

export const useTestCategory = (id: number | undefined) => {
    return useQuery<TestCategory>({
        queryKey: ['testCategory', id],
        queryFn: () => testCategoriesService.getById(id!),
        enabled: id != null,
        refetchOnWindowFocus: false,
    });
};

export const useCreateTestCategory = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (dto: CreateTestCategoryDto) => testCategoriesService.create(dto),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['testCategories'] }),
    });
};

export const useUpdateTestCategory = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, dto }: { id: number; dto: UpdateTestCategoryDto }) =>
            testCategoriesService.update(id, dto),
        onSuccess: (_, { id }) => {
            qc.invalidateQueries({ queryKey: ['testCategories'] });
            qc.invalidateQueries({ queryKey: ['testCategory', id] });
        },
    });
};

export const useDeleteTestCategory = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id: number) => testCategoriesService.remove(id),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['testCategories'] }),
    });
};
